import React, { useState, useMemo } from 'react';
import { Search, ExternalLink, X } from 'lucide-react';
import { SUPPLIERS } from '../consts';

const getUnderMaintenanceSuppliers = () => {
    const envValue = process.env.REACT_APP_UNDERMAINTENANCE_SUPPLIERS || '';
    return envValue
        .split(',')
        .map(s => s.trim().toLowerCase())
        .filter(s => s.length > 0);
};

const UNDERMAINTENANCE_SUPPLIERS = getUnderMaintenanceSuppliers();

const SupportedSuppliersModal = ({ isOpen, onClose }) => {
    const [searchTerm, setSearchTerm] = useState('');

    // Filter suppliers that support live scan
    const supportedSuppliers = useMemo(() => {
        return SUPPLIERS.filter(s => s.support_live_scan === true);
    }, []);

    // Filter based on search
    const filteredSuppliers = useMemo(() => {
        const active = supportedSuppliers.filter(s => !UNDERMAINTENANCE_SUPPLIERS.includes(s.id.toLowerCase()));
        if (!searchTerm.trim()) return active;
        const term = searchTerm.toLowerCase();
        return active.filter(s =>
            s.name.toLowerCase().includes(term) ||
            s.id.toLowerCase().includes(term)
        );
    }, [searchTerm, supportedSuppliers]);

    const filteredMaintenance = useMemo(() => {
        const maintenance = supportedSuppliers.filter(s => UNDERMAINTENANCE_SUPPLIERS.includes(s.id.toLowerCase()));
        if (!searchTerm.trim()) return maintenance;
        const term = searchTerm.toLowerCase();
        return maintenance.filter(s =>
            s.name.toLowerCase().includes(term) ||
            s.id.toLowerCase().includes(term)
        );
    }, [searchTerm, supportedSuppliers]);

    // Get supplier website URL
    const getSupplierUrl = (supplierId) => {
        const urlMap = {};
        return urlMap[supplierId] || `https://www.${supplierId}.com/`;
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[75vh] flex flex-col">
                {/* Header */}
                <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800">Supported Live Scan Suppliers</h2>
                        <p className="text-gray-600 text-sm mt-1">
                            {supportedSuppliers.length} suppliers available for live scanning (we continuously add new suppliers)
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-lg transition"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Search Bar */}
                <div className="p-4 border-b border-gray-200">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search suppliers..."
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        />
                    </div>
                </div>

                {/* Suppliers Grid */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {filteredSuppliers.map(supplier => {
                            const isUnderMaintenance = UNDERMAINTENANCE_SUPPLIERS.includes(supplier.id.toLowerCase());

                            return (
                                <a
                                    key={supplier.id}
                                    href={getSupplierUrl(supplier.id)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex flex-col items-center p-2 border rounded-lg transition group border-gray-200 hover:shadow-lg hover:border-teal-500"
                                >
                                    {supplier.logo && (
                                        <img
                                            src={supplier.logo}
                                            alt={supplier.name}
                                            className="w-16 h-16 object-contain mb-1"
                                            onError={(e) => e.target.style.display = 'none'}
                                        />
                                    )}
                                    <span className="text-sm font-semibold text-center transition text-gray-800 group-hover:text-teal-600">
                                        {supplier.name}
                                    </span>
                                    <ExternalLink
                                        size={14}
                                        className="text-gray-400 group-hover:text-teal-600 mt-2 transition"
                                    />
                                </a>
                            );
                        })}

                        {filteredMaintenance.length > 0 && (
                            <div className="flex flex-col p-2 border rounded-lg transition group border-gray-200 hover:shadow-lg hover:border-teal-500">
                                <p className="text-xs font-semibold text-orange-500 mb-2 text-start">🔧 Under Maintenance -</p>
                                <p className="text-xs font-semibold text-orange-500 mb-2">Use the Universal Live Scan!</p>
                                <div className="flex flex-wrap gap-x-6 gap-y-1 max-h-24 overflow-y-auto">
                                    {filteredMaintenance.map(supplier => (
                                        <span key={supplier.id} className="text-sm text-gray-300">
                                            {supplier.name}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {filteredSuppliers.length === 0 && (
                        <div className="text-center py-12">
                            <p className="text-gray-600">No suppliers found matching "{searchTerm}"</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-200 bg-gray-50">
                    <p className="text-sm text-gray-700 text-center">
                        <strong>Don't find your supplier?</strong> Send us an email at <strong>contact@multisellerpro.com</strong> and we will add it ASAP.
                    </p>
                    <p className="text-sm text-gray-700 text-center mt-2">
                        <strong>Want us to add a supplier but keep it <u>private for you</u>?</strong> Send us an email at <strong>contact@multisellerpro.com</strong> and we will help you ASAP.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SupportedSuppliersModal;